import { useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Repeat, Heart, Volume2, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useColorExtraction } from '@/hooks/use-color-extraction';
import { Slider } from '@/components/ui/slider';
import { TrackOptionsSheet } from './TrackOptionsSheet';
import type { RoxboxTrack } from '@/contexts/RoxboxPlayerContext';
import type { AudiobookPage } from '@/hooks/use-audiobooks';

interface DesktopPlayerBarProps {
  track: RoxboxTrack | null;
  currentAudiobookPage: AudiobookPage | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isLooping: boolean;
  canSkipNext: boolean;
  onTogglePlay: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (volume: number) => void;
  onToggleLoop: () => void;
  onPaywallHit?: () => void;
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function DesktopPlayerBar({
  track,
  currentAudiobookPage,
  isPlaying,
  currentTime,
  duration,
  volume,
  isLooping,
  canSkipNext,
  onTogglePlay,
  onPrevious,
  onNext,
  onSeek,
  onVolumeChange,
  onToggleLoop,
  onPaywallHit,
}: DesktopPlayerBarProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [optionsOpen, setOptionsOpen] = useState(false);
  
  const isAudiobookMode = !!currentAudiobookPage;
  const displayTitle = isAudiobookMode 
    ? `Page ${currentAudiobookPage.pageNumber}` 
    : track?.name || '';
  const displayArtist = isAudiobookMode 
    ? currentAudiobookPage.bookTitle 
    : track?.artist || '';
  const coverImage = isAudiobookMode 
    ? currentAudiobookPage.imageUrl 
    : track?.cover_image_url || '';
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  const { colors } = useColorExtraction(coverImage);

  // Like functionality
  const { data: likedTrackIds = [] } = useQuery({
    queryKey: ['likedTracks', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('liked_tracks')
        .select('track_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return data.map(lt => lt.track_id);
    },
    enabled: !!user,
  });

  const isLiked = track ? likedTrackIds.includes(track.id) : false;

  const likeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      await supabase.from('liked_tracks').insert({ user_id: user.id, track_id: trackId });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['likedTracks', user?.id] }),
  });

  const unlikeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      await supabase.from('liked_tracks').delete().eq('user_id', user.id).eq('track_id', trackId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['likedTracks', user?.id] }),
  });

  const toggleLike = () => {
    if (!user || !track) return;
    hapticLight();
    if (isLiked) unlikeMutation.mutate(track.id);
    else likeMutation.mutate(track.id);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(percent * duration);
  };

  if (!track && !currentAudiobookPage) return null;

  return (
    <>
      <div 
        className="fixed bottom-0 left-0 right-0 z-40 h-[90px] border-t border-white/10 backdrop-blur-xl"
        style={{
          background: `linear-gradient(180deg, ${colors.dominant}30 0%, rgba(0,0,0,0.95) 100%)`,
        }}
      >
        <div className="h-full flex items-center px-4 gap-4">
          {/* Left: Track Info */}
          <div className="flex items-center gap-3 w-[280px] shrink-0">
            <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 shadow-lg">
              <img 
                src={coverImage} 
                alt={displayTitle} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium text-sm truncate">{displayTitle}</p>
              <p className="text-white/50 text-xs truncate">{displayArtist || 'Unknown'}</p>
            </div>
            
            {/* Like button (tracks only) */}
            {user && !isAudiobookMode && track && (
              <button
                onClick={toggleLike}
                className={cn(
                  "p-2 transition-colors shrink-0",
                  isLiked ? "text-primary" : "text-white/40 hover:text-white"
                )}
              >
                <Heart className="w-4 h-4" fill={isLiked ? "currentColor" : "none"} />
              </button>
            )}
          </div>

          {/* Center: Controls + Progress */}
          <div className="flex-1 flex flex-col items-center justify-center max-w-[720px] mx-auto">
            {/* Controls */}
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={() => {
                  hapticLight();
                  onToggleLoop();
                }}
                className={cn(
                  "p-1.5 rounded-full transition-all",
                  isLooping ? "text-primary" : "text-white/40 hover:text-white"
                )}
              >
                <Repeat className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  hapticMedium();
                  onPrevious();
                }}
                className="text-white/60 hover:text-white transition-all p-1.5"
              >
                <SkipBack className="w-5 h-5" fill="currentColor" />
              </button>

              <button
                onClick={() => {
                  hapticMedium();
                  onTogglePlay();
                }}
                className="bg-white rounded-full p-2.5 text-black hover:scale-105 active:scale-95 transition-transform"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" fill="currentColor" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                )}
              </button>

              <button
                onClick={() => {
                  hapticMedium();
                  if (canSkipNext) onNext();
                  else onPaywallHit?.();
                }}
                className={cn(
                  "transition-all p-1.5",
                  canSkipNext ? "text-white/60 hover:text-white" : "text-white/20"
                )}
              >
                <SkipForward className="w-5 h-5" fill="currentColor" />
              </button>

              <button
                onClick={() => {
                  hapticLight();
                  setOptionsOpen(true);
                }}
                className="p-1.5 text-white/40 hover:text-white transition-colors"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="w-full flex items-center gap-2">
              <span className="text-[11px] text-white/50 w-10 text-right shrink-0">
                {formatTime(currentTime)}
              </span>
              <div 
                className="flex-1 relative h-1 bg-white/20 rounded-full cursor-pointer group"
                onClick={handleProgressClick}
              >
                <div
                  className="absolute top-0 left-0 h-full bg-white rounded-full group-hover:bg-primary transition-colors"
                  style={{ width: `${progress}%` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                  style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
                />
              </div>
              <span className="text-[11px] text-white/50 w-10 shrink-0">
                {formatTime(duration)}
              </span>
            </div>
          </div>

          {/* Right: Volume */}
          <div className="w-[180px] shrink-0 flex items-center justify-end gap-2">
            <Volume2 className="w-4 h-4 text-white/50" />
            <Slider
              value={[volume * 100]}
              onValueChange={([v]) => onVolumeChange(v / 100)}
              max={100}
              step={1}
              className="w-24"
            />
          </div>
        </div>
      </div>

      {/* Track Options Sheet */}
      <TrackOptionsSheet 
        open={optionsOpen} 
        onOpenChange={setOptionsOpen} 
        track={track}
        onSubscribe={onPaywallHit}
      />
    </>
  );
}
